import { useCallback, useMemo, useState } from 'react';

import { PURCHASE_STEP } from '~constants';
import { UpsellProposal } from '~types/appsync.types';

export interface UseUpsellOptions {
  availableUpsellings: UpsellProposal[];
  onNavigateTo: (step: PURCHASE_STEP) => void;
  onSelectUpselling: (upselling: UpsellProposal) => void;
}

export interface UseUpsellResult {
  hasAcceptedUpsell: boolean;
  shouldShowUpsellStep: boolean;
  handleSelectUpsell: (upselling: UpsellProposal) => void;
  handleSkipUpsell: () => void;
  handleBackFromUpsell: () => void;
  handleContinueFromUpsell: () => void;
  resetHasAcceptedUpsell: () => void;
}

const useUpsell = ({
  availableUpsellings,
  onNavigateTo,
  onSelectUpselling,
}: UseUpsellOptions): UseUpsellResult => {
  const [hasAcceptedUpsell, setHasAcceptedUpsell] = useState(false);

  const shouldShowUpsellStep = useMemo(
    () => availableUpsellings.length > 0 && !hasAcceptedUpsell,
    [availableUpsellings.length, hasAcceptedUpsell],
  );

  const handleSelectUpsell = useCallback(
    (upselling: UpsellProposal) => {
      setHasAcceptedUpsell(true);
      onSelectUpselling(upselling);
    },
    [onSelectUpselling],
  );

  const handleSkipUpsell = useCallback(() => {
    onNavigateTo(PURCHASE_STEP.OPTIONS);
  }, [onNavigateTo]);

  const handleContinueFromUpsell = useCallback(() => {
    onNavigateTo(PURCHASE_STEP.OPTIONS);
  }, [onNavigateTo]);

  const handleBackFromUpsell = useCallback(() => {
    onNavigateTo(PURCHASE_STEP.PRODUCT);
  }, [onNavigateTo]);

  const resetHasAcceptedUpsell = useCallback(() => {
    setHasAcceptedUpsell(false);
  }, []);

  return {
    handleBackFromUpsell,
    handleContinueFromUpsell,
    handleSelectUpsell,
    handleSkipUpsell,
    hasAcceptedUpsell,
    resetHasAcceptedUpsell,
    shouldShowUpsellStep,
  };
};

export default useUpsell;
